import React from "react";
import { StyledForm, ButtonGroup, BackButton, SubmitButton } from "./styles";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import { CustomFileUpload } from "../../../common/FormInputs";
import { CustomInputField } from "../../../common/FormInputs";
import { Popper } from "@material-ui/core";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import { splitFileValidationSchema } from "../../../../helpers/validationSchema";
import { CheckboxContainer } from "../../../common/FormInputs";
import { CustomCheckbox } from "../../../common/FormInputs";
import { CheckBoxLabel } from "../../../common/FormInputs";
import {
  Card,
  FormGrid,
  Label,
  PageWrapper,
  Section,
  StyledAutocomplete,
} from "./SplitAndUpload";

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const SplitAndDownload = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const upload_templates = [
    {
      name: "contractNo",
      label: "Upload Xlsx",
      type: "upload",
      required: true,
      fileName: "excel",
      accept: ".xlsx",
      types: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
    },
    {
      name: "pdfFile",
      label: "Pdf File",
      type: "upload",
      required: true,
      fileName: "pdf",
      accept: ".pdf",
      types: ["application/pdf"],
    },
  ];

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
          "application/pdf",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        if (accept === ".xlsx") {
          const response = await readExcelFile(file, fileName, setFieldValue);
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        } else if (accept === ".pdf") {
          const payload = {
            key: file.name,
          };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Successfully Uploaded", { variant: "success" });
          }
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer(); // Convert file to ArrayBuffer
    const workbook = XLSX.read(arrayBuffer, { type: "array" }); // Read the workbook
    const sheetName = workbook.SheetNames[0]; // Get the first sheet
    const sheet = workbook.Sheets[sheetName]; // Access the first sheet
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert to 2D array

    const headerRow = data[0]; // First row is the header
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    // // Extract values under "CONTRACT NUMBER" column
    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  /**
   * @description onFormSubmit
   */

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: `split and upload...` });
      const payload = {
        pdfFile: values.pdfFile,
        contractNo: values.contractNo.map(String),
        noticeCategory: values.noticeCategory,
        blankPages: values?.blankPages ? 1 : 0,
      };
      const response = await CaseService.splitDownload(payload);
      if (response?.fileUrl) {
        window.open(response?.fileUrl);
      }
      resetForm();
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            excel: "",
            contractNo: "",
            noticeCategory: "",
            pdfFile: "",
            blankPages: "",
            partyId: 10,
          }}
          validationSchema={splitFileValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
            resetForm,
          }) => (
            <StyledForm>
              <Section>
                <FormGrid>
                  <div>
                    <Label>Notice Category Name</Label>
                    <StyledAutocomplete
                      id="notice-category-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            placeholder="Select notice category..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div style={{ padding: "4px 0" }}>
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: "#6b7280",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("noticeCategory", newValue?.name || "")
                      }
                    />
                  </div>
                </FormGrid>
              </Section>
              <Section>
                <FormGrid>
                  {upload_templates.map((upload, index) => (
                    <div key={upload.name}>
                      <Label>
                        {upload.label} <span className="required-star"> *</span>
                      </Label>
                      <CustomFileUpload
                        error={
                          upload.accept === ".xlsx"
                            ? touched[upload.fileName] &&
                              errors[upload.fileName]
                            : touched[upload.name] && errors[upload.name]
                        }
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.types,
                            upload.accept,
                            values,
                            upload.fileName,
                          )
                        }
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                        accept={upload.accept}
                        file={{
                          name:
                            upload.fileName === "excel"
                              ? values[upload.fileName]
                              : upload.fileName === "pdf"
                                ? getFileName(values[upload.name])
                                : "",
                        }}
                      />
                    </div>
                  ))}
                </FormGrid>
                <div style={{ marginTop: 16 }}>
                  <CheckboxContainer>
                    <CustomCheckbox
                      id="split-download-blank-pages"
                      inputProps={{ id: "split-download-blank-pages" }}
                      name="blankPages"
                      checked={values.blankPages}
                      onChange={handleChange}
                    />
                    <CheckBoxLabel htmlFor="split-download-blank-pages">
                      With Blank Pages
                    </CheckBoxLabel>
                  </CheckboxContainer>
                </div>
              </Section>
              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

export default SplitAndDownload;
